import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatValue, formatPeriod, STATUS_META } from "@/lib/helpers";
import api from "@/lib/api";

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="font-medium text-foreground">{formatPeriod(label)}</p>
      <p className="text-muted-foreground">
        {formatValue(payload[0].value, unit)} {unit}
      </p>
    </div>
  );
}

export default function KpiDetailDialog({ kpiId, companyIdsParam, open, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !kpiId) return;
    setData(null);
    api
      .get(`/kpis/${kpiId}/history?company_ids=${companyIdsParam}`)
      .then((res) => setData(res.data));
  }, [open, kpiId, companyIdsParam]);

  const kpi = data?.kpi;
  const history = data?.history || [];
  const last = history[history.length - 1];
  const meta = STATUS_META[last?.status || "sin_datos"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="kpi-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{kpi?.name || "Indicador"}</DialogTitle>
          <DialogDescription>
            Tendencia histórica · {kpi?.category}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-end justify-between rounded-lg border border-border bg-accent/40 p-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Último periodo</p>
                <p className="mt-1 font-heading text-3xl font-bold tracking-tight">
                  {formatValue(last?.value, kpi?.unit)}
                  <span className="ml-1 text-sm font-normal text-muted-foreground">{kpi?.unit}</span>
                </p>
              </div>
              {last && (
                <Badge variant="outline" className={`${meta.bg} ${meta.text} ${meta.border}`}>
                  {meta.label}
                </Badge>
              )}
            </div>

            <div className="rounded-lg border border-border p-3">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="period"
                    tickFormatter={formatPeriod}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />
                  <Tooltip content={<ChartTooltip unit={kpi?.unit} />} />
                  <ReferenceLine y={kpi?.green_threshold} stroke="hsl(var(--success))" strokeDasharray="4 4" />
                  <ReferenceLine y={kpi?.yellow_threshold} stroke="hsl(var(--warning))" strokeDasharray="4 4" />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--info))"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "hsl(var(--info))" }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-lg border border-border bg-accent/30 p-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Fórmula</p>
              <p className="mt-1 font-mono text-sm">{kpi?.formula}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Meta {kpi?.comparison === "gte" ? "≥" : "≤"} {formatValue(kpi?.green_threshold, kpi?.unit)} ·
                Riesgo {kpi?.comparison === "gte" ? "≥" : "≤"} {formatValue(kpi?.yellow_threshold, kpi?.unit)}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
